import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { RetailOrg, RetailOrgsResponse } from '@jane/business-admin/types';
import { zRetailOrgsResponse } from '@jane/business-admin/types';
import {
  getRetailOrg,
  removeRetailOrg,
  setRetailOrg,
} from '@jane/business-admin/util';
import { janeApiV2, safeParseAsync } from '@jane/shared/data-access';

type fetchRetailOrgsParams = {
  query?: string;
};

/**
 * Fetching retail orgs from API
 */
const fetchRetailOrgs = async (
  params: fetchRetailOrgsParams
): Promise<RetailOrgsResponse> => {
  const urlParams = new URLSearchParams();
  params.query && urlParams.append('query', params.query);

  const url = `/business/retail_orgs?${urlParams.toString()}`;
  const response = await janeApiV2.get<RetailOrgsResponse>(url);

  return await safeParseAsync(zRetailOrgsResponse, response, 'fetchRetailOrgs');
};

export const useFetchRetailOrgs = (params: fetchRetailOrgsParams) =>
  useQuery({
    queryKey: ['retail_orgs', params],
    queryFn: () => fetchRetailOrgs(params),
    staleTime: Infinity,
  });

/**
 * Local storage for saving selected retail org
 */
const getSelectedOrgRequest = async (): Promise<
  RetailOrg | null | undefined
> => {
  return new Promise((res) => {
    const selectedOrg = getRetailOrg();
    return res(selectedOrg);
  });
};
export const useGetSelectedOrg = () =>
  useQuery({
    queryFn: getSelectedOrgRequest,
    queryKey: ['selected_retail_org'],
    // Query will be retried if no org is set, don't want that
    retry: false,
    staleTime: Infinity,
  });

const setSelectedOrgRequest = async (
  org: RetailOrg
): Promise<RetailOrg | null | undefined> => {
  return new Promise((res) => {
    setRetailOrg(org);
    return res(null);
  });
};
export const useSetSelectedOrg = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (org: RetailOrg) => setSelectedOrgRequest(org),
    onSuccess: () => {
      queryClient.invalidateQueries(['selected_retail_org']);
    },
  });
};

const removeSelectedOrgRequest = async (): Promise<
  string | null | undefined
> => {
  return new Promise((res) => {
    removeRetailOrg();
    return res(null);
  });
};
export const useRemoveSelectedOrg = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => removeSelectedOrgRequest(),
    onSuccess: () => {
      queryClient.invalidateQueries(['selected_retail_org']);
    },
  });
};
